import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SHELL_PALETTES, ShellPalette, ThemeMode } from './appShellTheme';

/** Also read by the inline script in index.html, which sets the mode before
 *  first paint so a light-mode user does not see a dark flash on load. */
export const THEME_MODE_STORAGE_KEY = 'nrve-theme-mode';

const readStoredMode = (): ThemeMode => {
  try {
    return window.localStorage.getItem(THEME_MODE_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

type ThemeModeContextValue = {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
};

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: 'dark',
  setMode: () => undefined,
  toggleMode: () => undefined
});

/**
 * Holds the light/dark choice. Light is the default; someone who switches to
 * dark keeps it, as the choice is remembered per browser.
 */
export const ThemeModeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(readStoredMode);

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    try {
      window.localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
    } catch {
      /* Private window or blocked storage: the choice just won't persist. */
    }
  }, [mode]);

  const toggleMode = useCallback(() => setMode((m) => (m === 'dark' ? 'light' : 'dark')), []);
  const value = useMemo(() => ({ mode, setMode, toggleMode }), [mode, toggleMode]);

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
};

export const useThemeMode = () => useContext(ThemeModeContext);

/** Resolved shell colours for places that cannot take a CSS `var()`, such as
 *  colours Recharts writes into SVG attributes. */
export const useShellPalette = (): ShellPalette => SHELL_PALETTES[useThemeMode().mode];
